/* DOGGO theme – storefront behaviour (no dependencies). */
(() => {
  'use strict';

  const theme = window.DoggoTheme || { routes: { cart: '/cart', cartAdd: '/cart/add', cartChange: '/cart/change', root: '/' }, moneyFormat: 'CHF {{amount}}', strings: {} };
  const designMode = !!(window.Shopify && window.Shopify.designMode);
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------------- */
  /* Helpers                                                                */
  /* ---------------------------------------------------------------------- */

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  function formatMoney(cents, format = theme.moneyFormat) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const value = Number(cents) || 0;
    const fmt = (n, dec, thou = "'", decSep = '.') => {
      const fixed = (n / 100).toFixed(dec);
      const [int, frac] = fixed.split('.');
      const withThousands = int.replace(/\B(?=(\d{3})+(?!\d))/g, thou);
      return frac ? withThousands + decSep + frac : withThousands;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount': return fmt(value, 2, ',', '.');
        case 'amount_no_decimals': return fmt(value, 0, ',', '.');
        case 'amount_with_comma_separator': return fmt(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(value, 2, "'", '.');
        case 'amount_no_decimals_with_space_separator': return fmt(value, 0, ' ', '.');
        case 'amount_with_space_separator': return fmt(value, 2, ' ', ',');
        default: return fmt(value, 2, ',', '.');
      }
    }).replace(/<[^>]*>/g, '');
  }

  function fill(template, data) {
    return String(template || '').replace(/\{(\w+)\}/g, (m, k) => (k in data ? data[k] : m));
  }

  /* Scroll lock with a counter so nested overlays never unlock too early.
     The body is pinned with position: fixed (works in iOS Safari) and the scroll position is restored afterwards. */
  const scrollLock = (() => {
    let count = 0;
    let y = 0;
    return {
      lock() {
        if (count++ > 0) return;
        y = window.scrollY || window.pageYOffset || 0;
        const gap = window.innerWidth - document.documentElement.clientWidth;
        const b = document.body.style;
        if (gap > 0) b.paddingRight = gap + 'px';
        b.position = 'fixed';
        /* Body zoom (desktop setting) scales the offset, so divide it back out. */
        const zoom = parseFloat(getComputedStyle(document.body).zoom) || 1;
        b.top = `-${y / zoom}px`;
        b.left = '0';
        b.right = '0';
        b.width = '100%';
        document.documentElement.classList.add('is-locked');
      },
      unlock() {
        if (count === 0) return;
        count -= 1;
        if (count > 0) return;
        const b = document.body.style;
        b.position = ''; b.top = ''; b.left = ''; b.right = ''; b.width = ''; b.paddingRight = '';
        document.documentElement.classList.remove('is-locked');
        const html = document.documentElement;
        const prev = html.style.scrollBehavior;
        html.style.scrollBehavior = 'auto';
        window.scrollTo(0, y);
        html.style.scrollBehavior = prev;
      }
    };
  })();

  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';

  function trapFocus(container, event) {
    const items = $$(FOCUSABLE, container).filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }

  /* Daily pseudo counter (seeded per day, grows over the day). */
  function ordersToday(min, max) {
    const now = new Date();
    const day = Math.floor((now - new Date(now.getFullYear(), 0, 0)) / 864e5);
    const seed = ((day * 9301 + 49297) % 233280) / 233280;
    const target = min + Math.round(seed * Math.max(0, max - min));
    const frac = 0.1 + 0.9 * ((now.getHours() * 60 + now.getMinutes()) / 1440);
    return Math.max(9, Math.round(target * frac));
  }

  function countdownParts(endAttr) {
    let end;
    if (endAttr) {
      end = new Date(endAttr);
    }
    if (!end || isNaN(end)) {
      const now = new Date();
      end = new Date(now);
      end.setHours(23, 59, 59, 0);
      if (end - now < 120000) end.setDate(end.getDate() + 1);
    }
    const d = Math.max(0, Math.floor((end - Date.now()) / 1000));
    const p = (n) => String(n).padStart(2, '0');
    return { h: p(Math.floor(d / 3600)), m: p(Math.floor((d % 3600) / 60)), s: p(d % 60) };
  }

  function tickLiveElements() {
    $$('[data-countdown]').forEach((el) => {
      const c = countdownParts(el.dataset.end);
      const h = $('[data-cd-h]', el); const m = $('[data-cd-m]', el); const s = $('[data-cd-s]', el);
      if (h) h.textContent = c.h; if (m) m.textContent = c.m; if (s) s.textContent = c.s;
    });
    $$('[data-countdown-inline]').forEach((el) => {
      const c = countdownParts();
      el.textContent = `${c.h}:${c.m}:${c.s}`;
    });
    $$('[data-orders-today]').forEach((el) => {
      el.textContent = ordersToday(Number(el.dataset.min) || 200, Number(el.dataset.max) || 300);
    });
  }

  /* ---------------------------------------------------------------------- */
  /* Drawers (mobile menu + cart)                                           */
  /* ---------------------------------------------------------------------- */

  const Drawers = {
    open(drawer, trigger) {
      if (!drawer || drawer.classList.contains('is-open')) return;
      Drawers.closeAll(drawer);
      drawer._trigger = trigger || document.activeElement;
      const popup = document.querySelector('popup-game:not([hidden])');
      if (popup && typeof popup.close === 'function') popup.close();
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('has-drawer-open');
      $$(`[aria-controls="${drawer.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
      scrollLock.lock();
      const panel = $('.drawer__panel', drawer);
      requestAnimationFrame(() => {
        const close = $('[data-drawer-close].close-x', drawer) || panel;
        close.focus({ preventScroll: true });
      });
    },
    close(drawer) {
      if (!drawer || !drawer.classList.contains('is-open')) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      if (!document.querySelector('[data-drawer].is-open')) document.body.classList.remove('has-drawer-open');
      $$(`[aria-controls="${drawer.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
      const panel = $('.drawer__panel', drawer);
      if (panel) panel.style.transform = '';
      scrollLock.unlock();
      const t = drawer._trigger;
      if (t && document.body.contains(t)) t.focus({ preventScroll: true });
    },
    closeAll(except) {
      $$('[data-drawer].is-open').forEach((d) => { if (d !== except) Drawers.close(d); });
    }
  };

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      e.preventDefault();
      Drawers.open(document.getElementById(opener.dataset.drawerOpen), opener);
      return;
    }
    const closer = e.target.closest('[data-drawer-close]');
    if (closer) {
      e.preventDefault();
      Drawers.close(closer.closest('[data-drawer]'));
    }
  });

  document.addEventListener('keydown', (e) => {
    const open = $('[data-drawer].is-open');
    if (!open) return;
    if (e.key === 'Escape') { e.preventDefault(); Drawers.close(open); }
    if (e.key === 'Tab') trapFocus($('.drawer__panel', open), e);
  });

  /* Swipe to close for drawers (direction depends on side). */
  function enableSwipe(drawer) {
    const panel = $('.drawer__panel', drawer);
    if (!panel || panel._swipe) return;
    panel._swipe = true;
    const dir = drawer.classList.contains('drawer--left') ? -1 : 1;
    let x0 = null; let y0 = null; let dx = 0; let dragging = false;
    panel.addEventListener('touchstart', (e) => {
      if (e.touches.length !== 1) return;
      x0 = e.touches[0].clientX; y0 = e.touches[0].clientY; dx = 0; dragging = false;
    }, { passive: true });
    panel.addEventListener('touchmove', (e) => {
      if (x0 === null) return;
      const mx = e.touches[0].clientX - x0; const my = e.touches[0].clientY - y0;
      if (!dragging) {
        if (Math.abs(mx) > 12 && Math.abs(mx) > Math.abs(my) * 1.4 && Math.sign(mx) === dir) {
          dragging = true; drawer.classList.add('is-dragging');
        } else if (Math.abs(my) > 12) { x0 = null; return; }
      }
      if (dragging) { dx = Math.max(0, mx * dir); panel.style.transform = `translateX(${dx * dir}px)`; }
    }, { passive: true });
    const end = () => {
      if (dragging) {
        drawer.classList.remove('is-dragging');
        if (dx > Math.min(120, panel.offsetWidth * 0.3)) Drawers.close(drawer);
        else panel.style.transform = '';
      }
      x0 = null; dragging = false; dx = 0;
    };
    panel.addEventListener('touchend', end);
    panel.addEventListener('touchcancel', end);
  }

  /* ---------------------------------------------------------------------- */
  /* Cart                                                                   */
  /* ---------------------------------------------------------------------- */

  const Cart = {
    busy: Promise.resolve(),
    drawer() { return document.getElementById('CartDrawer'); },

    queue(fn) {
      Cart.busy = Cart.busy.then(fn, fn);
      return Cart.busy;
    },

    sectionIds() { return Cart.drawer() ? ['cart-drawer'] : []; },

    /* Accepts a cart object or a number. The shipping-protection line is not counted as a product. */
    protectionId() {
      const drawer = Cart.drawer();
      return drawer ? Number(drawer.dataset.protectionVariant || 0) : 0;
    },

    /* Reads /cart.js; returns null when the request fails (e.g. rate limited) instead of throwing. */
    async fetchCart() {
      try {
        const res = await fetch(`${theme.routes.cart}.js`, { credentials: 'same-origin' });
        return res.ok ? await res.json() : null;
      } catch (err) { return null; }
    },

    /* Shipping protection alone makes no sense: remove it once no product is left. */
    async dropOrphanProtection(cart) {
      const id = Cart.protectionId();
      if (!cart || !id) return cart;
      const items = cart.items || [];
      const prot = items.find((i) => i.variant_id === id);
      if (!prot || items.some((i) => i.variant_id !== id)) return cart;
      const res = await fetch(`${theme.routes.cartChange}.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: prot.key, quantity: 0, sections: Cart.sectionIds(), sections_url: window.location.pathname })
      });
      if (!res.ok) return cart;
      const data = await res.json();
      Cart.render(data.sections);
      return data;
    },

    updateCount(cart) {
      let count = cart;
      if (cart && typeof cart === 'object') {
        const protectionId = Cart.protectionId();
        count = (cart.items || []).reduce((n, item) => n + (item.variant_id === protectionId ? 0 : item.quantity), 0);
      }
      $$('[data-cart-count]').forEach((el) => { el.textContent = count; });
    },

    render(sections) {
      const drawer = Cart.drawer();
      if (!drawer || !sections || !sections['cart-drawer']) return;
      const doc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      const fresh = doc.getElementById('CartDrawerContent');
      const current = document.getElementById('CartDrawerContent');
      if (fresh && current) {
        const body = $('.cart-drawer__body', current);
        const scroll = body ? body.scrollTop : 0;
        current.replaceWith(fresh);
        const newBody = $('.cart-drawer__body', fresh);
        if (newBody) newBody.scrollTop = scroll;
        tickLiveElements();
        if (drawer.classList.contains('is-open') && !drawer.contains(document.activeElement)) {
          const close = $('[data-drawer-close].close-x', drawer) || $('.drawer__panel', drawer);
          if (close) close.focus({ preventScroll: true });
        }
      }
    },

    async refresh() {
      if (!Cart.drawer()) return;
      const res = await fetch(`${theme.routes.root}?sections=cart-drawer`, { credentials: 'same-origin' });
      if (res.ok) Cart.render(await res.json());
      const cart = await Cart.fetchCart();
      if (cart) Cart.updateCount(cart);
    },

    open(trigger) {
      const drawer = Cart.drawer();
      if (!drawer || theme.cartType === 'page') { window.location.href = theme.routes.cart; return; }
      enableSwipe(drawer);
      Drawers.open(drawer, trigger);
    },

    add(items) {
      return Cart.queue(async () => {
        const body = { items, sections: Cart.sectionIds(), sections_url: window.location.pathname };
        const res = await fetch(`${theme.routes.cartAdd}.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body)
        });
        const data = await res.json().catch(() => ({ status: res.status, description: theme.strings.addError }));
        if (!res.ok || data.status) {
          throw new Error(data.description || data.message || theme.strings.addError || 'Error');
        }
        Cart.render(data.sections);
        if (theme.cartType === 'page') { window.location.href = theme.routes.cart; return data; }
        if (theme.openCartAfterAdd !== false) Cart.open();
        /* Count update runs after the drawer is already open, so the drawer does not wait for a second request. */
        Cart.fetchCart().then((cart) => {
          if (cart) Cart.updateCount(cart);
          document.dispatchEvent(new CustomEvent('doggo:cart-updated', { detail: cart }));
        });
        return data;
      });
    },

    change(key, quantity, row) {
      if (row) row.classList.add('is-busy');
      return Cart.queue(async () => {
        try {
          const res = await fetch(`${theme.routes.cartChange}.js`, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: key, quantity, sections: Cart.sectionIds(), sections_url: window.location.pathname })
          });
          const data = await res.json().catch(() => ({ status: res.status, description: theme.strings.addError }));
          if (!res.ok || data.status) throw new Error(data.description || data.message);
          Cart.render(data.sections);
          const cleaned = await Cart.dropOrphanProtection(data);
          Cart.updateCount(cleaned);
          document.dispatchEvent(new CustomEvent('doggo:cart-updated', { detail: data }));
        } catch (err) {
          if (row) row.classList.remove('is-busy');
          const box = $('[data-cart-error]');
          if (box) { box.textContent = err.message || 'Error'; box.hidden = false; }
        }
      });
    }
  };
  window.DoggoCart = Cart;
  window.DoggoUI = { formatMoney, scrollLock, trapFocus, Drawers };

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-cart-open]');
    if (opener && theme.cartType !== 'page' && Cart.drawer()) {
      e.preventDefault();
      Cart.open(opener);
      return;
    }
    const prot = e.target.closest('[data-protection-toggle]');
    if (prot) {
      e.preventDefault();
      /* Flip the switch at once; the server update follows and re-renders the drawer. */
      prot.classList.add('is-busy');
      prot.classList.toggle('is-on', !prot.dataset.key);
      const done = () => prot.classList.remove('is-busy');
      if (prot.dataset.key) Cart.change(prot.dataset.key, 0).finally(done);
      else Cart.add([{ id: Number(prot.dataset.variant), quantity: 1 }]).catch((err) => {
        const box = $('[data-cart-error]');
        if (box) { box.textContent = err.message; box.hidden = false; }
      }).finally(done);
      return;
    }
    const checkout = e.target.closest('.cdr__checkout');
    if (checkout) { checkout.classList.add('is-loading'); return; }
    const qtyBtn = e.target.closest('[data-cart-qty]');
    if (qtyBtn) {
      e.preventDefault();
      const quantity = Math.max(0, parseInt(qtyBtn.dataset.cartQty, 10) || 0);
      Cart.change(qtyBtn.dataset.key, quantity, qtyBtn.closest('.cart-item'));
    }
  });

  /* Keep cart state fresh when returning via the back/forward cache. */
  window.addEventListener('pageshow', (e) => { if (e.persisted) Cart.refresh().catch(() => {}); });

  /* ---------------------------------------------------------------------- */
  /* Mega menu (hover intent + keyboard)                                    */
  /* ---------------------------------------------------------------------- */

  function initMega() {
    const trigger = $('[data-mega-trigger]');
    const mega = $('[data-mega]');
    if (!trigger || !mega || !window.matchMedia('(hover: hover)').matches) return;
    const link = $('a', trigger);
    let openT; let closeT;
    const show = () => { clearTimeout(closeT); openT = setTimeout(() => { mega.hidden = false; link.setAttribute('aria-expanded', 'true'); }, 220); };
    const hide = () => { clearTimeout(openT); closeT = setTimeout(() => { mega.hidden = true; link.setAttribute('aria-expanded', 'false'); }, 260); };
    [trigger, mega].forEach((el) => { el.addEventListener('mouseenter', show); el.addEventListener('mouseleave', hide); });
    link.addEventListener('focus', () => { clearTimeout(closeT); mega.hidden = false; link.setAttribute('aria-expanded', 'true'); });
    mega.addEventListener('focusout', (e) => { if (!mega.contains(e.relatedTarget) && e.relatedTarget !== link) hide(); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !mega.hidden) { mega.hidden = true; link.focus(); } });
  }

  /* ---------------------------------------------------------------------- */
  /* Native dialogs                                                         */
  /* ---------------------------------------------------------------------- */

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-dialog-open]');
    if (opener) {
      const dlg = document.getElementById(opener.dataset.dialogOpen);
      if (dlg && typeof dlg.showModal === 'function') {
        dlg.showModal();
        scrollLock.lock();
        dlg.addEventListener('close', () => scrollLock.unlock(), { once: true });
      }
      return;
    }
    const closer = e.target.closest('[data-dialog-close]');
    if (closer) { closer.closest('dialog').close(); return; }
    if (e.target.tagName === 'DIALOG' && e.target.open) e.target.close();
  });

  /* ---------------------------------------------------------------------- */
  /* Marquees + reveal                                                      */
  /* ---------------------------------------------------------------------- */

  function initObservers() {
    if (!('IntersectionObserver' in window)) {
      $$('[data-reveal]').forEach((el) => el.classList.add('is-in'));
      $$('[data-wait]').forEach((el) => el.classList.add('is-in'));
      return;
    }

    const marqueeObs = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        en.target.classList.toggle('is-paused', !en.isIntersecting);
        const t = $('[data-wait]', en.target);
        if (t && en.isIntersecting) t.classList.add('is-in');
      });
    }, { rootMargin: '120px' });
    $$('[data-marquee]').forEach((m) => marqueeObs.observe(m));

    if (designMode || reduceMotion || !document.body.classList.contains('has-reveal')) {
      $$('[data-reveal]').forEach((el) => el.classList.add('is-in'));
      return;
    }
    const vh = window.innerHeight;
    const revealObs = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (en.isIntersecting) { en.target.classList.add('is-in'); revealObs.unobserve(en.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    const groups = new Map();
    $$('[data-reveal]').forEach((el) => {
      if (el.getBoundingClientRect().top < vh) { el.classList.add('is-in'); return; }
      const parent = el.parentElement;
      const i = groups.get(parent) || 0;
      groups.set(parent, i + 1);
      el.style.setProperty('--rd', Math.min(i, 5) * 80 + 'ms');
      revealObs.observe(el);
    });
  }

  /* ---------------------------------------------------------------------- */
  /* Custom elements                                                        */
  /* ---------------------------------------------------------------------- */

  class IngredientSlider extends HTMLElement {
    connectedCallback() {
      this.items = $$('.ing__item', this);
      this.imgs = $$('.ing__img', this);
      this.label = $('[data-ing-index]', this);
      this.index = 0;
      const total = this.items.length;
      this.pad = (n) => String(n).padStart(2, '0');
      $('[data-ing-prev]', this)?.addEventListener('click', () => this.go((this.index + total - 1) % total));
      $('[data-ing-next]', this)?.addEventListener('click', () => this.go((this.index + 1) % total));
      let x0 = null;
      const media = $('.ing__media', this);
      media?.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
      media?.addEventListener('touchend', (e) => {
        if (x0 === null) return;
        const dx = e.changedTouches[0].clientX - x0; x0 = null;
        if (Math.abs(dx) > 40) this.go(dx < 0 ? (this.index + 1) % total : (this.index + total - 1) % total);
      });
      if (designMode) {
        document.addEventListener('shopify:block:select', (e) => {
          const i = this.items.findIndex((it) => it.contains(e.target) || it === e.target);
          if (i >= 0) this.go(i);
        });
      }
    }
    go(i) {
      this.index = i;
      this.items.forEach((it, k) => it.classList.toggle('is-active', k === i));
      this.imgs.forEach((im, k) => im.classList.toggle('is-active', k === i));
      if (this.label) this.label.textContent = `${this.pad(i + 1)} / ${this.pad(this.items.length)}`;
    }
  }

  class StatRings extends HTMLElement {
    connectedCallback() {
      const items = $$('[data-stat]', this);
      const C = 2 * Math.PI * 52;
      items.forEach((it) => { const n = $('[data-num]', it); if (n) n.textContent = '0%'; });
      const run = () => {
        const t0 = performance.now(); const dur = reduceMotion ? 1 : 1500;
        const step = (t) => {
          const k = Math.min(1, (t - t0) / dur); const e = 1 - Math.pow(1 - k, 3);
          items.forEach((it) => {
            const target = parseFloat(it.dataset.stat) || 0;
            const num = $('[data-num]', it); const ring = $('[data-ring]', it);
            if (num) num.textContent = Math.round(target * e) + '%';
            if (ring) ring.setAttribute('stroke-dasharray', `${(C * target / 100 * e).toFixed(1)} ${C.toFixed(1)}`);
          });
          if (k < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      };
      if (!('IntersectionObserver' in window)) { run(); return; }
      const obs = new IntersectionObserver((es) => { if (es.some((x) => x.isIntersecting)) { obs.disconnect(); run(); } }, { threshold: 0.35 });
      obs.observe(this);
    }
  }

  class QuoteRotator extends HTMLElement {
    connectedCallback() {
      this.items = $$('.quote-rot__item', this);
      if (this.items.length < 2) return;
      const ms = (parseFloat(this.dataset.interval) || 8.5) * 1000;
      let i = 0;
      this.timer = setInterval(() => {
        if (document.hidden) return;
        this.items[i].classList.remove('is-active');
        i = (i + 1) % this.items.length;
        void this.items[i].offsetWidth;
        this.items[i].classList.add('is-active');
      }, ms);
    }
    disconnectedCallback() { clearInterval(this.timer); }
  }

  class VideoCard extends HTMLElement {
    connectedCallback() {
      this.video = $('video', this);
      if (!this.video) return;
      $('[data-video-toggle]', this)?.addEventListener('click', () => this.toggle());
      this.video.addEventListener('click', () => this.toggle());
      $('[data-video-mute]', this)?.addEventListener('click', (e) => {
        e.stopPropagation();
        VideoCard.muted = !VideoCard.muted;
        this.video.muted = VideoCard.muted;
        this.syncMute();
      });
      this.video.addEventListener('pause', () => this.classList.remove('is-playing'));
    }
    syncMute() { const b = $('[data-video-mute]', this); if (b) b.textContent = this.video.muted ? '🔇' : '🔊'; }
    toggle() {
      const v = this.video;
      if (!v.paused) { v.pause(); return; }
      $$('video-card').forEach((c) => { if (c !== this && c.video && !c.video.paused) c.video.pause(); });
      v.muted = VideoCard.muted;
      v.loop = true;
      v.play().then(() => { this.classList.add('is-playing'); this.syncMute(); }).catch(() => {
        v.muted = true; VideoCard.muted = true;
        v.play().then(() => { this.classList.add('is-playing'); this.syncMute(); }).catch(() => {});
      });
    }
  }
  VideoCard.muted = true;

  class PillarTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = $$('[data-pillar-tab]', this);
      this.tabs.forEach((t) => t.addEventListener('click', () => this.select(Number(t.dataset.pillarTab))));
      this.tabs.forEach((t, i) => t.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          const n = (i + (e.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
          this.select(n); this.tabs[n].focus();
        }
      }));
      if ('IntersectionObserver' in window) {
        const obs = new IntersectionObserver((es) => {
          es.forEach((en) => { const v = $('[data-pillar-media].is-active video', this); if (!v) return; if (en.isIntersecting) v.play().catch(() => {}); else v.pause(); });
        }, { threshold: 0.2 });
        obs.observe(this);
      }
      if (designMode) {
        document.addEventListener('shopify:block:select', (e) => {
          const i = this.tabs.indexOf(e.target.closest ? e.target.closest('[data-pillar-tab]') : null);
          if (i >= 0) this.select(i);
        });
      }
    }
    select(i) {
      this.tabs.forEach((t, k) => t.setAttribute('aria-selected', String(k === i)));
      $$('[data-pillar-panel]', this).forEach((p, k) => p.classList.toggle('is-active', k === i));
      $$('[data-pillar-media]', this).forEach((m, k) => {
        m.classList.toggle('is-active', k === i);
        const v = $('video', m);
        if (!v) return;
        if (k === i) { v.preload = 'auto'; v.currentTime = 0; v.play().catch(() => {}); } else v.pause();
      });
    }
  }

  class ReviewList extends HTMLElement {
    connectedCallback() {
      this.cards = $$('.rev-card', this);
      this.grid = $('[data-rev-grid]', this);
      this.count = $('[data-rev-count]', this);
      this.filter = 'all';
      $$('[data-filter]', this).forEach((b) => b.addEventListener('click', () => {
        $$('[data-filter]', this).forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
        this.filter = b.dataset.filter; this.limit = this.step; this.apply();
      }));
      $$('[data-sort]', this).forEach((b) => b.addEventListener('click', () => {
        $$('[data-sort]', this).forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
        this.sort(b.dataset.sort);
      }));
      /* Show a few reviews first, the rest on demand (shorter page, faster path to checkout). */
      this.step = designMode ? 0 : Number(this.dataset.limit) || 0;
      this.limit = this.step;
      this.moreWrap = $('[data-rev-more-wrap]', this);
      $('[data-rev-more]', this)?.addEventListener('click', () => { this.limit += this.step; this.apply(); });
      this.apply();
    }
    apply() {
      let n = 0;
      let visible = 0;
      $$('.rev-card', this.grid).forEach((c) => {
        const match = this.filter === 'all' || c.dataset.cat === this.filter;
        if (match) n++;
        const show = match && (!this.limit || n <= this.limit);
        c.hidden = !show;
        if (show) visible++;
      });
      if (this.count) this.count.textContent = n;
      if (this.moreWrap) this.moreWrap.hidden = !(this.limit && n > visible);
    }
    sort(mode) {
      const val = (c, k) => Number(c.dataset[k]) || 0;
      const sorted = this.cards.slice().sort((a, b) => {
        if (mode === 'newest') return val(a, 'days') - val(b, 'days');
        if (mode === 'best') return (val(b, 'rating') - val(a, 'rating')) || (val(b, 'helpful') - val(a, 'helpful'));
        return val(b, 'helpful') - val(a, 'helpful');
      });
      sorted.forEach((c) => this.grid.appendChild(c));
      this.apply();
    }
  }

  class ArticleHero extends HTMLElement {
    connectedCallback() {
      this.imgs = $$('img', this);
      this.dots = $$('.art-hero__dots span', this);
      if (this.imgs.length < 2) return;
      this.i = 0;
      $('[data-hero-prev]', this)?.addEventListener('click', () => this.go(this.i - 1));
      $('[data-hero-next]', this)?.addEventListener('click', () => this.go(this.i + 1));
    }
    go(i) {
      const n = this.imgs.length; this.i = (i + n) % n;
      this.imgs.forEach((im, k) => { im.classList.toggle('is-active', k === this.i); if (k === this.i) im.loading = 'eager'; });
      this.dots.forEach((d, k) => d.classList.toggle('is-active', k === this.i));
    }
  }

  class TrackingForm extends HTMLElement {
    connectedCallback() {
      const form = $('form', this); const err = $('[data-tracking-error]', this);
      form?.addEventListener('submit', (e) => {
        e.preventDefault();
        const code = (form.code.value || '').trim().replace(/\s+/g, '');
        if (code.length < 6) { err.hidden = false; return; }
        err.hidden = true;
        window.open(form.dataset.url.replace('{code}', encodeURIComponent(code)), '_blank', 'noopener');
      });
    }
  }

  class FeedingCalculator extends HTMLElement {
    connectedCallback() {
      this.range = $('[data-calc-range]', this);
      this.rows = $$('[data-row]', this);
      this.tiers = $$('[data-tier]', this);
      this.steps = $$('[data-calc-step]', this);
      this.range?.addEventListener('input', () => { this.show('form'); this.update(); });
      $('[data-calc-run]', this)?.addEventListener('click', () => { this.show('load'); setTimeout(() => this.result(), reduceMotion ? 50 : 1100); });
      $('[data-calc-reset]', this)?.addEventListener('click', () => this.show('form'));
      $('[data-calc-add]', this)?.addEventListener('click', (e) => this.add(e.currentTarget));
      this.update();
    }
    tierIndex() {
      const kg = Number(this.range.value);
      const i = this.rows.findIndex((r) => kg <= (Number(r.dataset.max) || Infinity));
      return i === -1 ? this.rows.length - 1 : i;
    }
    update() {
      const kg = Number(this.range.value);
      const max = Number(this.range.max);
      $('[data-calc-kg]', this).textContent = kg >= max ? `${max}+` : kg;
      const i = this.tierIndex();
      this.tiers.forEach((t, k) => t.classList.toggle('is-on', k === i));
    }
    show(step) { this.steps.forEach((s) => { s.hidden = s.dataset.calcStep !== step; }); }
    result() {
      const row = this.rows[this.tierIndex()];
      if (!row) return;
      const chews = Number(row.dataset.chews) || 1;
      const days = Math.round((Number(this.dataset.days) || 90) / chews);
      $('[data-calc-size]', this).textContent = row.dataset.label;
      $('[data-calc-chews]', this).textContent = `${chews} Chew${chews > 1 ? 's' : ''}`;
      $('[data-calc-supply]', this).textContent = fill(this.dataset.supplyTemplate, { days });
      const perBox = Number(this.dataset.subPrice || this.dataset.price) || 0;
      const perDay = Math.round((perBox * chews) / (Number(this.dataset.days) || 90));
      $('[data-calc-daily]', this).textContent = fill(this.dataset.perDayTemplate, { price: formatMoney(perDay) });
      this.chews = chews;
      this.show('done');
    }
    add(btn) {
      const id = Number(this.dataset.variant);
      if (!id) return;
      const item = { id, quantity: this.chews || 1 };
      if (this.dataset.plan) item.selling_plan = Number(this.dataset.plan);
      btn.classList.add('is-loading');
      Cart.add([item]).catch((err) => alert(err.message)).finally(() => btn.classList.remove('is-loading'));
    }
  }

  /* ---------------------------------------------------------------------- */
  /* Buy box                                                                */
  /* ---------------------------------------------------------------------- */

  class BuyBox extends HTMLElement {
    connectedCallback() {
      try { this.cfg = JSON.parse($('[data-buybox-config]', this).textContent); } catch (e) { return; }
      const c = this.cfg;
      this.form = $('form.buy__form', this) || $('form', this);
      this.state = {
        weight: c.weight.value,
        dogs: 1,
        plan: c.defaultPlan === 'sub' && c.hasSub ? 'sub' : 'once',
        trial: false,
        variant: this.initialVariant()
      };
      this.thresholds = (c.thresholds || []).map(Number).filter((n) => !isNaN(n));
      this.tiers = $$('[data-bb-tier]', this);
      this.jarTpl = $('template[data-bb-jar]', this);
      this.benefitTemplates = new Map();
      $$('[data-bb="trial-benefits"] .plan__benefit span:last-child', this).forEach((el) => this.benefitTemplates.set(el, el.textContent));

      this.bindWeight();
      this.bindPlans();
      this.bindVariants();
      this.bindSubmit();
      this.bindGallery();
      this.bindSticky();
      this.update();
    }

    disconnectedCallback() {
      if (this.sticky && this.sticky.parentNode === document.body) this.sticky.remove();
      document.body.classList.remove('has-sticky-atc');
    }

    initialVariant() {
      const id = Number($('[data-bb-variant]', this)?.value);
      return this.cfg.variants.find((v) => v.id === id) || this.cfg.variants[0];
    }

    boxesFor(kg) {
      let n = 1;
      this.thresholds.forEach((t) => { if (kg > t) n++; });
      return n;
    }

    bindWeight() {
      const range = $('[data-bb-weight]', this);
      range?.addEventListener('input', () => { this.state.weight = Number(range.value); this.update(); });
      this.tiers.forEach((b) => b.addEventListener('click', () => {
        const kg = Number(b.dataset.kg);
        if (!kg) return;
        this.state.weight = kg;
        if (range) range.value = kg;
        this.update();
      }));
      $$('[data-bb-dogs]', this).forEach((b) => b.addEventListener('click', () => {
        const next = this.state.dogs + Number(b.dataset.bbDogs);
        this.state.dogs = Math.min(this.cfg.maxDogs || 9, Math.max(1, next));
        this.update();
      }));
    }

    bindPlans() {
      this.addEventListener('click', (e) => {
        const pick = e.target.closest('[data-plan-pick]');
        if (pick && this.contains(pick)) {
          this.setPlan(pick.dataset.planPick);
          const menu = $('[data-sticky-menu]', this);
          if (menu && !menu.hidden) this.toggleStickyMenu(false);
        }
      });
      const trial = $('[data-bb-trial]', this);
      if (trial) {
        const toggle = () => { this.state.trial = !this.state.trial; this.update(); };
        trial.addEventListener('click', toggle);
        trial.addEventListener('keydown', (e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); toggle(); } });
      }
    }

    setPlan(plan) {
      if (plan === 'sub' && !this.cfg.hasSub) return;
      if (plan === 'once' && this.cfg.requiresPlan) return;
      this.state.plan = plan;
      this.update();
    }

    bindVariants() {
      const inputs = $$('[data-bb-option]', this);
      if (!inputs.length) return;
      inputs.forEach((inp) => inp.addEventListener('change', () => {
        const selected = [];
        inputs.forEach((i) => { if (i.checked) selected[Number(i.dataset.bbOption)] = i.value; });
        const match = this.cfg.variants.find((v) => v.options.every((o, k) => o === selected[k]));
        if (match) { this.state.variant = match; this.update(); }
      }));
    }

    bindSubmit() {
      if (!this.form) return;
      this.form.addEventListener('submit', (e) => {
        if (!Cart.drawer() && theme.cartType !== 'page') return;
        e.preventDefault();
        this.addToCart($('[data-bb-submit]', this));
      });
    }

    addToCart(btn) {
      const v = this.state.variant;
      if (!v || !v.available) return;
      const item = { id: v.id, quantity: this.effectiveQty() };
      if (this.state.plan === 'sub' && this.cfg.subPlanId) item.selling_plan = this.cfg.subPlanId;
      const err = $('[data-bb="error"]', this);
      if (err) err.hidden = true;
      btn?.classList.add('is-loading');
      Cart.add([item])
        .catch((e) => { if (err) { err.textContent = e.message; err.hidden = false; } })
        .finally(() => btn?.classList.remove('is-loading'));
    }

    boxes() { return this.boxesFor(this.state.weight) * this.state.dogs; }

    effectiveQty() {
      const boxes = this.boxes();
      return this.state.plan === 'once' && this.state.trial && boxes > 1 ? 1 : boxes;
    }

    update() {
      const c = this.cfg; const t = c.t; const s = this.state; const v = s.variant;
      const boxes = this.boxes();
      const chews = boxes;
      const days = c.days || 90;
      const boxWord = boxes === 1 ? t.boxSingular : t.boxPlural;
      const onceBox = v.price;
      const subBox = v.sub != null ? v.sub : null;
      const anchorBox = c.anchorPerBox > 0 ? c.anchorPerBox : (v.compare > onceBox ? v.compare : onceBox);
      const trialOn = s.plan === 'once' && s.trial && boxes > 1;
      const trialDays = Math.max(1, Math.round(days / boxes));
      const qty = trialOn ? 1 : boxes;
      const effDays = trialOn ? trialDays : days;

      const onceTotal = onceBox * qty;
      const subTotal = subBox != null ? subBox * boxes : null;
      const anchorTotal = anchorBox * boxes;
      const data = {
        boxes, chews, s: chews > 1 ? 's' : '', box_word: boxWord, days, trial_days: trialDays,
        chews_total: chews * effDays
      };

      const roots = this.sticky ? [this, this.sticky] : [this];
      const set = (key, value, html = false) => {
        roots.forEach((root) => $$(`[data-bb="${key}"]`, root).forEach((el) => { if (html) el.innerHTML = value; else el.textContent = value; }));
      };

      /* Weight */
      set('weight', s.weight);
      set('dogs', s.dogs);
      set('result', fill(t.result, data), true);
      const giant = $('[data-bb="giant"]', this);
      if (giant) giant.hidden = !(s.weight > c.giant);
      const tierIdx = this.boxesFor(s.weight) - 1;
      this.tiers.forEach((b, k) => { const on = k === tierIdx; b.classList.toggle('is-on', on); b.setAttribute('aria-pressed', String(on)); });
      const tierName = ((c.tierNames || [])[tierIdx] || '').trim();
      set('tier-name', tierName || s.weight + ' kg');

      /* Once card */
      const pct = anchorBox > onceBox ? Math.round(((anchorBox - onceBox) / anchorBox) * 100) : 0;
      set('once-anchor', anchorBox > onceBox ? formatMoney(anchorTotal) : '');
      set('once-tag', pct > 1 ? fill(t.onceTag, { pct }) : '');
      $$('[data-bb="once-tag"], [data-bb="once-anchor"]', this).forEach((el) => { (el.closest('.plan__tag, .plan__anchor') || el).style.display = el.textContent ? '' : 'none'; });
      set('once-price', formatMoney(onceTotal));
      set('once-perday', formatMoney(Math.round(onceTotal / effDays)));
      const word = (n) => (n === 1 ? t.boxSingular : t.boxPlural);
      this.fillJars('once', qty);
      set('once-get', fill(t.onceGet || '', { ...data, boxes: qty, box_word: word(qty) }));
      set('once-get-sub', trialOn ? (t.trialGetSub || '') : (t.onceGetSub || ''));
      set('once-detail', fill(t.onceDetail, { ...data, boxes: qty, box_word: qty === 1 ? t.boxSingular : t.boxPlural, days: effDays, chews_total: chews * effDays }));

      const trialEl = $('[data-bb-trial]', this);
      if (trialEl) {
        trialEl.hidden = !(c.trialEnabled && boxes > 1);
        trialEl.classList.toggle('is-on', trialOn);
        trialEl.setAttribute('aria-checked', String(trialOn));
        set('trial-note', fill(trialOn ? t.trialOn : t.trialOff, data));
      }
      const onceBen = $('[data-bb="once-benefits"]', this);
      const trialBen = $('[data-bb="trial-benefits"]', this);
      if (onceBen && trialBen) {
        onceBen.hidden = trialOn; trialBen.hidden = !trialOn;
        this.benefitTemplates.forEach((tpl, el) => { el.textContent = fill(tpl, data); });
      }

      /* Subscription card */
      if (subTotal != null) {
        set('sub-anchor', formatMoney(anchorTotal));
        set('sub-price', formatMoney(subTotal));
        set('sub-perday', formatMoney(Math.round(subTotal / days)));
        this.fillJars('sub', boxes);
        set('sub-get', fill(t.subGet || '', { ...data, box_word: word(boxes) }));
        set('sub-get-sub', t.subGetSub || '');
        set('sub-detail', fill(t.subDetail, { ...data, chews_total: chews * days }));
        const onceFull = onceBox * boxes;
        const diff = onceFull - subTotal;
        set('saving', diff > 100 ? fill(t.saving, { amount: formatMoney(diff), pct: Math.round((diff / onceFull) * 100) }) : '');
      }
      $$('[data-bb="saving"]', this).forEach((el) => { el.style.display = el.textContent ? '' : 'none'; });

      /* Selected plan */
      $$('[data-plan-card]', this).forEach((card) => {
        const on = card.dataset.planCard === s.plan;
        card.classList.toggle('is-selected', on);
        const r = $('.plan__pick', card); if (r) r.setAttribute('aria-checked', String(on));
      });
      const total = s.plan === 'sub' && subTotal != null ? subTotal : onceTotal;
      const totalDays = s.plan === 'sub' ? days : effDays;
      set('summary', fill(t.summary, { ...data, total: formatMoney(total), days: totalDays, per_day: formatMoney(Math.round(total / totalDays)), chews_total: chews * totalDays }));

      const low = $('[data-bb="low"]', this);
      if (low) low.hidden = !(qty < (c.lowStockBoxes || 0));

      /* Reassurance rows that differ per plan */
      $$('[data-bb-once-text]', this).forEach((el) => { el.textContent = s.plan === 'once' ? el.dataset.bbOnceText : el.dataset.bbSubText; });

      /* CTA + hidden inputs */
      const submit = $('[data-bb-submit]', this);
      const ctaText = !v.available ? t.soldOut : (trialOn ? t.ctaTrial : fill(t.cta, data));
      set('cta', ctaText);
      if (submit) {
        submit.disabled = !v.available;
        submit.classList.toggle('btn--gold', s.plan !== 'once');
        submit.classList.toggle('btn--gold-green', s.plan === 'once');
      }
      const idInput = $('[data-bb-variant]', this); if (idInput) idInput.value = v.id;
      const qtyInput = $('[data-bb-qty]', this); if (qtyInput) qtyInput.value = qty;
      const planInput = $('[data-bb-plan]', this);
      if (planInput) {
        const useSub = s.plan === 'sub' && c.subPlanId;
        planInput.disabled = !useSub;
        planInput.value = useSub ? c.subPlanId : '';
      }

      /* Sticky bar */
      const planLabel = s.plan === 'sub' ? t.stickySubLabel : t.stickyOnceLabel;
      set('sticky-sub', fill(t.stickySub, { ...data, plan_label: planLabel }));
      set('sticky-toggle', s.plan === 'sub' ? t.stickyToggleSub : t.stickyToggleOnce);
      set('sticky-off', s.plan === 'sub' ? t.stickySubOff : t.stickyOnceOff);
      set('sticky-cta', !v.available ? t.soldOut : fill(t.stickyCta, { per_day: formatMoney(Math.round(total / totalDays)) }));
      $$('.sticky-atc__opt', this.sticky || this).forEach((o) => o.setAttribute('aria-checked', String(o.dataset.planPick === s.plan)));
      const stickyCta = $('[data-sticky-cta]', this.sticky || this);
      if (stickyCta) {
        stickyCta.disabled = !v.available;
        stickyCta.classList.toggle('btn--gold', s.plan !== 'once');
        stickyCta.classList.toggle('btn--gold-green', s.plan === 'once');
      }
    }

    fillJars(kind, n) {
      const box = $('[data-bb-jars="' + kind + '"]', this);
      if (!box) return;
      if (!this.jarTpl) { box.hidden = true; return; }
      const count = Math.min(n, 8);
      if (box.childElementCount === count) return;
      box.textContent = '';
      for (let k = 0; k < count; k++) box.appendChild(this.jarTpl.content.cloneNode(true));
    }

    /* Gallery: slide track + thumbnails + swipe */
    bindGallery() {
      const track = $('[data-gallery-track]', this);
      if (!track) return;
      const slides = $$('.gallery__slide', track);
      const thumbs = $$('[data-gallery-thumb]', this);
      const strip = $('[data-gallery-thumbs]', this);
      let i = 0;
      const go = (n) => {
        i = (n + slides.length) % slides.length;
        track.style.transform = `translateX(-${i * 100}%)`;
        slides.forEach((sl, k) => { sl.setAttribute('aria-hidden', String(k !== i)); const img = $('img', sl); if (k === i && img) img.loading = 'eager'; });
        thumbs.forEach((th, k) => th.setAttribute('aria-current', String(k === i)));
        const th = thumbs[i];
        if (th && strip) {
          const left = th.offsetLeft - strip.clientWidth / 2 + th.clientWidth / 2;
          strip.scrollTo({ left, behavior: reduceMotion ? 'auto' : 'smooth' });
        }
      };
      this.galleryGo = go;
      $('[data-gallery-prev]', this)?.addEventListener('click', () => go(i - 1));
      $('[data-gallery-next]', this)?.addEventListener('click', () => go(i + 1));
      thumbs.forEach((th) => th.addEventListener('click', () => go(Number(th.dataset.galleryThumb))));
      const main = $('[data-gallery-main]', this);
      let x0 = null; let y0 = null;
      main.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; y0 = e.touches[0].clientY; }, { passive: true });
      main.addEventListener('touchend', (e) => {
        if (x0 === null) return;
        const dx = e.changedTouches[0].clientX - x0; const dy = e.changedTouches[0].clientY - y0; x0 = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) go(dx < 0 ? i + 1 : i - 1);
      });
      go(0);
    }

    /* Sticky add-to-cart bar */
    bindSticky() {
      this.sticky = $('[data-sticky-atc]', this);
      if (!this.sticky) return;
      /* Move the bar to <body> so no ancestor (transform/zoom/overflow) can break position: fixed. */
      document.body.appendChild(this.sticky);
      this.sticky.addEventListener('click', (e) => {
        const pick = e.target.closest('[data-plan-pick]');
        if (pick) { this.setPlan(pick.dataset.planPick); this.toggleStickyMenu(false); }
      });
      $('[data-sticky-cta]', this.sticky)?.addEventListener('click', (e) => this.addToCart(e.currentTarget));
      $('[data-sticky-toggle]', this.sticky)?.addEventListener('click', () => this.toggleStickyMenu());
      document.addEventListener('click', (e) => {
        const pick = $('[data-sticky-pick]', this.sticky);
        if (pick && !pick.contains(e.target)) this.toggleStickyMenu(false);
      });
      const check = () => {
        /* Landing pages: show as soon as the main button is gone. Product + home: once the whole buy box is passed. */
        const ref = this.dataset.stickyFrom === 'cta' ? ($('[data-bb-submit]', this) || this) : this;
        const show = ref.getBoundingClientRect().bottom < 0;
        if (show === this.stickyShown) return;
        this.stickyShown = show;
        this.sticky.classList.toggle('is-visible', show);
        this.sticky.setAttribute('aria-hidden', String(!show));
        $$('button', this.sticky).forEach((b) => { b.tabIndex = show ? 0 : -1; });
        document.body.classList.toggle('has-sticky-atc', show);
        if (!show) this.toggleStickyMenu(false);
      };
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(check, { threshold: [0, 0.01] }).observe(this);
      }
      window.addEventListener('scroll', () => { if (!this._raf) this._raf = requestAnimationFrame(() => { this._raf = null; check(); }); }, { passive: true });
      check();
      this.fitStickyMedia();
    }

    toggleStickyMenu(force) {
      const menu = $('[data-sticky-menu]', this.sticky || this);
      const toggle = $('[data-sticky-toggle]', this.sticky || this);
      if (!menu) return;
      const open = force === undefined ? menu.hidden : force;
      menu.hidden = !open;
      if (toggle) toggle.setAttribute('aria-expanded', String(open));
    }

    /* Keep the sticky gallery below the sticky header and inside the viewport. */
    fitStickyMedia() {
      const media = $('[data-buy-media]', this);
      if (!media) return;
      const fit = () => {
        const header = $('.section-header .site-header') || $('.landing-header') || $('.section-header');
        const head = (header ? header.offsetHeight : 0) + 16;
        const top = Math.min(head, window.innerHeight - media.offsetHeight - 16);
        media.style.setProperty('--sticky-top', Math.max(-media.offsetHeight + 120, top) + 'px');
      };
      fit();
      window.addEventListener('resize', fit);
      if ('ResizeObserver' in window) new ResizeObserver(fit).observe(media);
    }
  }

  /* ---------------------------------------------------------------------- */
  /* Popup game                                                             */
  /* ---------------------------------------------------------------------- */

  class PopupGame extends HTMLElement {
    connectedCallback() {
      this.key = this.dataset.key || 'doggoPopSeen';
      this.needed = Number(this.dataset.flips) || 3;
      this.flips = 0;
      const posted = $('[data-posted]', this);
      let seen = false;
      try { seen = localStorage.getItem(this.key) === '1'; } catch (e) { /* storage unavailable */ }
      $$('[data-flip]', this).forEach((f) => f.addEventListener('click', () => this.flip(f)));
      $$('[data-popup-close]', this).forEach((b) => b.addEventListener('click', () => this.close()));
      $$('[data-popup-finish]', this).forEach((b) => b.addEventListener('click', () => this.close()));
      $$('[data-popup-copy]', this).forEach((b) => b.addEventListener('click', () => this.copy(b)));
      this.klaviyo = this.dataset.klaviyoKey && this.dataset.klaviyoList;
      this.hasPhone = !!$('[data-step="phone"]', this);
      const email = $('[data-popup-email]', this);
      const err = $('[data-popup-error]', this);
      $('[data-popup-next]', this)?.addEventListener('click', (e) => {
        if (e.currentTarget.type !== 'button') return;
        const value = (email?.value || '').trim();
        if (!/^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i.test(value)) {
          if (err) err.hidden = false;
          email?.focus();
          return;
        }
        if (err) err.hidden = true;
        this.email = value;
        if (this.klaviyo) this.subscribe({ email: value });
        this.step(this.hasPhone ? 'phone' : 'done');
        if (!this.hasPhone) this.markSeen();
      });
      $('[data-popup-phone-submit]', this)?.addEventListener('click', (e) => {
        const phone = this.normalizePhone($('[data-popup-phone]', this)?.value);
        if (this.klaviyo) {
          if (phone) this.subscribe({ email: this.email, phone });
          this.step('done');
          this.markSeen();
        } else if (!phone) {
          const p = $('[data-popup-phone]', this); if (p) p.value = '';
        }
        if (e.currentTarget.type === 'button') e.preventDefault();
      });
      $('[data-popup-skip-phone]', this)?.addEventListener('click', (e) => {
        const p = $('[data-popup-phone]', this); if (p) p.value = '';
        if (this.klaviyo) { e.preventDefault(); this.step('done'); this.markSeen(); }
      });
      $('form', this)?.addEventListener('submit', (e) => {
        if (this.klaviyo) { e.preventDefault(); return; }
        this.markSeen();
      });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); if (e.key === 'Tab') trapFocus(this, e); });
      this.updateHint();

      if (posted) { this.open(); return; }
      if (this.dataset.designMode === 'true') {
        document.addEventListener('shopify:section:select', (e) => { if (this.closest('.shopify-section') === e.target) this.open(); });
        document.addEventListener('shopify:section:deselect', (e) => { if (this.closest('.shopify-section') === e.target) this.close(false); });
        return;
      }
      if (!seen) this.timer = setTimeout(() => this.open(), (Number(this.dataset.delay) || 8) * 1000);
    }
    open() {
      if (!this.hidden) return;
      if ($('[data-drawer].is-open')) { this.timer = setTimeout(() => this.open(), 4000); return; }
      this._trigger = document.activeElement;
      this.hidden = false;
      document.body.classList.add('popup-open');
      scrollLock.lock();
      requestAnimationFrame(() => $('[data-popup-close]', this)?.focus({ preventScroll: true }));
    }
    close(mark = true) {
      if (this.hidden) return;
      this.hidden = true;
      document.body.classList.remove('popup-open');
      scrollLock.unlock();
      if (mark) this.markSeen();
      if (this._trigger && document.body.contains(this._trigger)) this._trigger.focus({ preventScroll: true });
    }
    markSeen() { try { localStorage.setItem(this.key, '1'); } catch (e) { /* storage unavailable */ } }
    flip(card) {
      if (card.classList.contains('is-open') || this.flips >= this.needed) return;
      card.classList.add('is-open');
      this.flips++;
      this.updateHint();
      if (this.flips >= this.needed) setTimeout(() => this.step('win'), reduceMotion ? 100 : 1100);
    }
    updateHint() {
      const h = $('[data-popup-hint]', this);
      if (!h) return;
      const left = Math.max(0, this.needed - this.flips);
      if (this.flips === 0) h.textContent = h.dataset.start || '';
      else h.textContent = left === 0 ? h.dataset.done : fill(h.dataset.template, { n: left });
    }
    /* Swiss-friendly E.164: "079 123 45 67" → "+41791234567". */
    normalizePhone(raw) {
      let p = String(raw || '').replace(/[^\d+]/g, '');
      if (!p) return '';
      if (p.startsWith('00')) p = '+' + p.slice(2);
      if (!p.startsWith('+')) p = (this.dataset.countryCode || '+41') + p.replace(/^0/, '');
      return /^\+\d{8,15}$/.test(p) ? p : '';
    }
    /* Klaviyo client API: subscribes the profile to the list (email and, if given, SMS marketing). */
    subscribe({ email, phone }) {
      if (!email) return Promise.resolve();
      const subscriptions = { email: { marketing: { consent: 'SUBSCRIBED' } } };
      const attributes = { email, subscriptions };
      if (phone) {
        attributes.phone_number = phone;
        subscriptions.sms = { marketing: { consent: 'SUBSCRIBED' } };
      }
      const body = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: this.dataset.klaviyoSource || 'Popup',
            profile: { data: { type: 'profile', attributes } }
          },
          relationships: { list: { data: { type: 'list', id: this.dataset.klaviyoList } } }
        }
      };
      return fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(this.dataset.klaviyoKey)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/vnd.api+json', Accept: 'application/vnd.api+json', revision: '2024-10-15' },
        body: JSON.stringify(body)
      }).catch(() => {});
    }
    copy(btn) {
      const code = btn.dataset.code || '';
      const done = () => { btn.textContent = btn.dataset.copied || code; };
      if (navigator.clipboard) navigator.clipboard.writeText(code).then(done, done);
      else done();
    }
    step(name) {
      $$('.popup__step', this).forEach((s) => s.classList.toggle('is-active', s.dataset.step === name));
      const input = $(`.popup__step[data-step="${name}"] input`, this);
      if (input) input.focus({ preventScroll: true });
    }
  }

  /* ---------------------------------------------------------------------- */
  /* Article content → landing-page rows                                    */
  /* ---------------------------------------------------------------------- */

  function landingifyArticle() {
    const root = $('[data-landing-content]');
    if (!root) return;
    const nodes = Array.from(root.children);
    const groups = [];
    let current = null;
    nodes.forEach((node, idx) => {
      const isKicker = node.tagName === 'H6';
      const startsRow = node.tagName === 'H2' || (isKicker && nodes[idx + 1] && nodes[idx + 1].tagName === 'H2');
      if (startsRow && !(node.tagName === 'H2' && nodes[idx - 1] && nodes[idx - 1].tagName === 'H6')) {
        current = { nodes: [] };
        groups.push(current);
      }
      if (current) current.nodes.push(node);
    });
    let flip = false;
    groups.forEach((g) => {
      const imgNode = g.nodes.find((n) => n.tagName === 'IMG' || n.querySelector?.('img'));
      if (!imgNode) return;
      const img = imgNode.tagName === 'IMG' ? imgNode : imgNode.querySelector('img');
      const row = document.createElement('div');
      row.className = 'art-row' + (flip ? ' art-row--flip' : '');
      const media = document.createElement('div');
      media.className = 'art-row__img';
      img.loading = 'lazy';
      img.removeAttribute('width'); img.removeAttribute('height');
      media.appendChild(img);
      const text = document.createElement('div');
      text.className = 'art-row__text';
      g.nodes[0].before(row);
      g.nodes.forEach((n) => { if (n === imgNode && n.tagName === 'IMG') return; if (n !== imgNode || n.textContent.trim()) text.appendChild(n); else n.remove(); });
      row.appendChild(media);
      row.appendChild(text);
      flip = !flip;
    });
    root.classList.add('is-landing');
  }

  /* ---------------------------------------------------------------------- */
  /* Advertorial: hero slider + gold bar that drops in after the header     */
  /* ---------------------------------------------------------------------- */

  class AdvSlider extends HTMLElement {
    connectedCallback() {
      this.slides = $$('[data-slide]', this);
      this.dots = $$('.adv-slider__dots span', this);
      this.index = 0;
      if (this.slides.length < 2) return;
      $('[data-slide-prev]', this)?.addEventListener('click', () => this.go(this.index - 1));
      $('[data-slide-next]', this)?.addEventListener('click', () => this.go(this.index + 1));
      let x0 = null;
      this.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
      this.addEventListener('touchend', (e) => {
        if (x0 === null) return;
        const dx = e.changedTouches[0].clientX - x0; x0 = null;
        if (Math.abs(dx) > 40) this.go(this.index + (dx < 0 ? 1 : -1));
      });
      if (designMode) {
        document.addEventListener('shopify:block:select', (e) => {
          const i = this.slides.indexOf(e.target);
          if (i >= 0) this.go(i);
        });
      }
    }
    go(n) {
      const total = this.slides.length;
      this.index = (n + total) % total;
      this.slides.forEach((s, k) => s.classList.toggle('is-active', k === this.index));
      this.dots.forEach((d, k) => d.classList.toggle('is-active', k === this.index));
    }
  }

  function initAdvDrop() {
    const bar = $('[data-adv-drop]');
    const header = $('[data-adv-header]');
    if (!bar || !header || designMode) return;
    const link = $('a', bar);
    let shown = false;
    const check = () => {
      const buy = document.getElementById('kaufen');
      const vh = window.innerHeight;
      const passedHeader = header.getBoundingClientRect().bottom <= 0;
      const atBuy = buy ? buy.getBoundingClientRect().top < vh * (window.innerWidth > 760 ? 0.6 : 0.85) : false;
      const show = passedHeader && !atBuy;
      if (show === shown) return;
      shown = show;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', String(!show));
      if (link) link.tabIndex = show ? 0 : -1;
    };
    window.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check);
    check();
  }

  /* ---------------------------------------------------------------------- */
  /* Boot                                                                   */
  /* ---------------------------------------------------------------------- */

  const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };
  define('ingredient-slider', IngredientSlider);
  define('stat-rings', StatRings);
  define('quote-rotator', QuoteRotator);
  define('video-card', VideoCard);
  define('pillar-tabs', PillarTabs);
  define('review-list', ReviewList);
  define('article-hero', ArticleHero);
  define('tracking-form', TrackingForm);
  define('feeding-calculator', FeedingCalculator);
  define('buy-box', BuyBox); /* legacy buy box, kept for old templates */
  define('popup-game', PopupGame);
  define('adv-slider', AdvSlider);
  define('cart-drawer', class extends HTMLElement { connectedCallback() { enableSwipe(this); } });

  function boot() {
    tickLiveElements();
    setInterval(tickLiveElements, 1000);
    initMega();
    initObservers();
    landingifyArticle();
    initAdvDrop();
    const menu = document.getElementById('MobileMenu');
    if (menu) enableSwipe(menu);
    /* Close the mobile menu when the viewport grows to desktop. */
    window.matchMedia('(min-width: 641px)').addEventListener?.('change', (e) => { if (e.matches) Drawers.close(menu); });
    /* Open the cart when arriving from a "?cart=open" link. */
    if (/[?&]cart=open/.test(window.location.search)) Cart.open();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  /* Theme editor: re-run observers when sections reload. */
  document.addEventListener('shopify:section:load', () => { initObservers(); tickLiveElements(); });

  /* Theme editor: marquees stand still and scroll to the selected block so the editor outline matches the card. */
  if (designMode) {
    document.addEventListener('shopify:block:select', (e) => {
      const box = e.target.closest && e.target.closest('.marquee');
      if (!box) return;
      const r = e.target.getBoundingClientRect(); const b = box.getBoundingClientRect();
      box.scrollTo({ left: box.scrollLeft + r.left - b.left - (b.width - r.width) / 2, behavior: 'smooth' });
    });
  }
})();

/* The cart drawer counts products without the shipping-protection line; copy that number to every counter on load. */
(() => {
  const drawerCount = document.querySelector('.cdr__count');
  if (!drawerCount) return;
  document.querySelectorAll('[data-cart-count]').forEach((el) => { el.textContent = drawerCount.textContent.trim(); });
})();
